/**
 * FormattedTextLink component.
 * This component takes an array of parsed parts (strings and tokens) and a token map,
 * then renders them as React components, converting tokens into interactive elements or styled text.
 *
 * @module ui/components/FormattedTextLink
 * @memberof -Common
 */

import './FormattedTextLink.scss';

import React from 'react';

import PropTypes from 'prop-types';

import Icon from '@ulta/core/components/Icon/Icon';
import { Text } from '@ulta/core/components/Text/Text';
import { hasItems } from '@ulta/core/utils/array/array';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

import Button from '@ulta/modules/Button/Button';

export const FORMATTED_LINK_TYPE = {
  Link: 'link',
  Text: 'text',
  Icon: 'icon'
};


/**
 * FormattedTextLink component
 * @param {FormattedTextLinkProps} props - React properties passed from composition
 * @returns {React.ReactElement}
 */
const FormattedTextLink = ( props ) => {
  const { parsedParts, tokenMap, className } = props;
  if( !hasItems( parsedParts ) ){
    return null;
  }

  const renderPart = ( partObj, index ) => {
    if( partObj.isToken ){
      const tokenName = partObj.value;
      const tokenData = tokenMap?.[tokenName];
      switch ( tokenData?.type ){
        case FORMATTED_LINK_TYPE.Link:
          return (
            <Button
              likeLink
              key={ index }
              ariaLabel={ tokenData.value?.accessibility }
              size='default'
              action={ tokenData.value }
              label={ tokenData.value?.label }
            />
          );
        case FORMATTED_LINK_TYPE.Text:
          return (
            <Text key={ index } htmlTag='span' textStyle='body' color={ tokenData.color }>
              { tokenData.value }
            </Text>
          );
        case FORMATTED_LINK_TYPE.Icon:
          return (
            <span key={ index } className={ tokenData.value?.wrapperClassName }>
              <Icon
                name={ typeof tokenData.value === 'string' ? tokenData.value : tokenData.value?.name }
                size={ tokenData.value?.size }
                className={ tokenData.value?.className }
                aria-label={ tokenData.value?.ariaLabel }
              />
            </span>
          );
        default:
          return null;
      }
    }
    return (
      <Text key={ index } htmlTag='span' textStyle='body'>
        { partObj.value }
      </Text>
    );
  };

  return (
    <span className={ className || 'FormattedTextLink__wrapper' }>
      { parsedParts.map( renderPart ) }
    </span>
  );
};


/**
 * Property type definitions for FormattedTextLink.
 * @typedef {object} FormattedTextLinkProps
 * @property {array} parsedParts - An array of parsed parts, typically generated by the `parse` function.
 * @property {object} tokenMap - A map where keys are token names (e.g., 'link') and values are objects containing `type` and `value`.
 */
const propTypes = {
  className: PropTypes.string,
  parsedParts: PropTypes.arrayOf( PropTypes.shape( {
    isToken: PropTypes.bool.isRequired,
    value: PropTypes.string.isRequired,
    raw: PropTypes.string.isRequired
  } ) ).isRequired,
  tokenMap: PropTypes.objectOf( PropTypes.shape( {
    type: PropTypes.oneOf( Object.values( FORMATTED_LINK_TYPE ) ).isRequired,
    value: PropTypes.oneOfType( [
      PropTypes.string,
      PropTypes.shape( {
        url: PropTypes.string,
        label: PropTypes.string,
        name: PropTypes.string,
        size: PropTypes.string,
        className: PropTypes.string,
        wrapperClassName: PropTypes.string,
        ariaLabel: PropTypes.string,
        accessibility: PropTypes.shape( {
          label: PropTypes.string
        } )
      } )
    ] ).isRequired,
    color: PropTypes.string
  } ) ).isRequired
};

FormattedTextLink.propTypes = propTypes;

export default FormattedTextLink;

/**
 * Splits the string by tokens, and returns an array of objects with token information.
 * @param {object} data - The data object.
 * @returns {array} An array of objects, each with `isToken`, `value`, and `raw` properties.
 */
export function parse( data ){
  const { str } = handleEmptyObjects( data ) ;
  if( !str ){
    return [];
  }
  const parts = str.split( /(\{[^}]+\})/g ).filter( Boolean );
  return parts.map( part => {
    if( part.startsWith( '{' ) && part.endsWith( '}' ) ){
      return {
        isToken: true,
        value: part.slice( 1, -1 ),
        raw: part
      };
    }
    return {
      isToken: false,
      value: part,
      raw: part
    };
  } );
}
